/**
 * Очередь заданий на отсмотр — экран сотрудника.
 *
 * Сотрудник не видит общий пул и скрытый вид результата. Сервер выдаёт ровно
 * одно задание, пока предыдущее не завершено или не закрыто по технической
 * невозможности просмотра.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Eye,
  Flag,
  MapPin,
  Plus,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { PeriodSelector } from '@/components/analytics'
import { ActionButton, Badge, Button, Card, ToneBadge, ToneDot } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { PriorityPartnerPicker } from '@/components/pedagogy/PriorityPartnerPicker'
import {
  PriorityReviewRequestFields,
  type PriorityReviewRequestFilters,
} from '@/components/pedagogy/PriorityReviewRequestFields'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { formatPedagogySystemTimestamp } from '@/lib/pedagogyTime'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { PEDAGOGY_SKIP_REASON_TONE } from '@/lib/pedagogySkipReasonTone'
import {
  getMyPedagogyReviewWorkload,
  listPedagogyReviewTechnicalCases,
  listMyPedagogyReviewAssignments,
  markPedagogyReviewAssignmentRead,
  pedagogyReasonPausesStudio,
  reportPedagogyReviewTechnicalIssue,
  requestNextPedagogyReviewAssignment,
  resolvePedagogyReviewTechnicalCase,
  type PedagogyReviewAssignment,
  type PedagogyReviewSkipReason,
  type PedagogyReviewTechnicalCase,
  type PedagogyReviewTechnicalCaseAction,
  type PedagogyReviewTechnicalVerificationResult,
  type PedagogyReviewWorkload,
} from '@/services/pedagogyReviewQueueService'
import {
  cancelPedagogyPriorityRequest,
  createPedagogyPriorityRequest,
  getPedagogyPriorityRequestManagementOptions,
  listPedagogyPriorityRequestPartners,
  listPedagogyPriorityRequests,
  type PedagogyPriorityManagementOptions,
  type PedagogyPriorityPartnerOption,
  type PedagogyPriorityRequestRegistryRow,
} from '@/services/pedagogyPriorityRequestService'

const EMPTY_PRIORITY_FILTERS: PriorityReviewRequestFilters = {
  teacherId: '',
  studioInternalId: '',
  directionName: '',
  trainingDate: '',
  itemId: '',
  deferredActivation: false,
  availableFrom: '',
}

// hint — что специалист обязан проверить до выбора причины; на сервер не уходит.
const SKIP_REASONS: ReadonlyArray<{ code: PedagogyReviewSkipReason; label: string; hint: string }> = [
  { code: 'recording_problem', label: 'Запись отсутствует или не открывается', hint: 'Камера в студии работает, дата и время занятия сверены, архив открыт — записи именно этого занятия нет' },
  { code: 'no_camera', label: 'Нет камеры или она не работает', hint: 'В зале нет камеры, она выключена или не пишет — студия приостановится на 5 дней, другие её занятия не придут' },
  { code: 'no_access', label: 'Нет доступа к камерам или архиву', hint: 'Вход в систему видеонаблюдения студии не проходит или архив закрыт' },
  { code: 'lesson_mismatch', label: 'Занятие не найдено или данные не совпадают', hint: 'Запись есть, но в это время шло другое занятие или его не было' },
  { code: 'queue_selection_error', label: 'Занятие или студия не должны попадать в выдачу', hint: 'Дубль, уже отсмотрено, неверное направление или педагог; студия закрыта или работает онлайн — опишите ниже' },
]

// Красная зона — не причина пропуска, а категория сопровождения 4 франчайзи (20261009170000,
// старые пометки специалистов удалены в 20261009200000).

type ManagerWorkspaceTab = 'technical' | 'requests'
type TechnicalFilter = 'all' | PedagogyReviewSkipReason | 'excluded'

function formatDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function formatTime(value: string): string {
  return value.slice(0, 5)
}

function formatDueDate(value: string): string {
  return formatPedagogySystemTimestamp(value, false)
}

function formatDateTime(value: string): string {
  return formatPedagogySystemTimestamp(value)
}

function localIsoDate(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  return new Date(now.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

function priorityStatusLabel(status: PedagogyPriorityRequestRegistryRow['status']): string {
  if (status === 'open') return 'Ожидает'
  if (status === 'assigned') return 'В работе'
  if (status === 'done') return 'Выполнен'
  if (status === 'failed') return 'Не выполнен'
  return 'Отменён'
}

function priorityStatusVariant(status: PedagogyPriorityRequestRegistryRow['status']): 'success' | 'warning' | 'neutral' | 'error' {
  if (status === 'done') return 'success'
  if (status === 'assigned') return 'warning'
  if (status === 'failed') return 'error'
  return 'neutral'
}

function technicalReasonShortLabel(code: PedagogyReviewSkipReason): string {
  if (code === 'recording_problem') return 'Нет записи'
  if (code === 'no_access') return 'Нет доступа'
  if (code === 'no_camera') return 'Нет камеры'
  if (code === 'lesson_mismatch') return 'Не найдено'
  return 'Ошибка выдачи'
}

function priorityFailureLabel(code: string | null): string | null {
  if (!code) return null
  if (['no_recording', 'camera_not_recording', 'recording_unavailable', 'recording_problem'].includes(code)) {
    return 'Запись отсутствует или не открывается'
  }
  if (code === 'no_access') return 'Нет доступа к камерам или архиву'
  if (code === 'no_camera') return 'Нет камеры или она не работает'
  if (code === 'lesson_mismatch') return 'Занятие не найдено или данные не совпадают'
  if (code === 'queue_selection_error') return 'Занятие или студия не должны попадать в выдачу'
  if (code === 'technical_quarantine') return 'Подходящие занятия недоступны из-за технического карантина'
  if (code === 'no_requestable_sessions') return 'Подходящих занятий в семидневном окне больше нет'
  return 'Технически выполнить отсмотр не удалось'
}

function AssignmentCard({
  assignment,
  onOpen,
  onSkip,
}: {
  assignment: PedagogyReviewAssignment
  onOpen: (assignment: PedagogyReviewAssignment) => void
  onSkip: (assignment: PedagogyReviewAssignment) => void
}) {
  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>
                {formatDate(assignment.trainingDate)}, {formatTime(assignment.trainingTime)}
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="min-w-0">
                {assignment.cityName ? `${assignment.cityName}, ` : ''}
                {assignment.studioAddress || `студия №${assignment.studioNumber}`}
              </span>
            </div>
          </div>

          <Badge variant={assignment.isOverdue ? 'error' : 'neutral'}>
            {assignment.isOverdue ? 'Срок истёк' : `до ${formatDueDate(assignment.dueAt)}`}
          </Badge>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {assignment.directionNameRaw && <span>{assignment.directionNameRaw}</span>}
          {assignment.groupNameRaw && <span>{assignment.groupNameRaw}</span>}
          {assignment.attendeesCount != null && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {assignment.attendeesCount}
            </span>
          )}
        </div>

        {assignment.teacherNameRaw && (
          <div className="text-sm">
            <span className="text-muted-foreground">Педагог по расписанию: </span>
            <span className="text-foreground">{assignment.teacherNameRaw}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" onClick={() => onOpen(assignment)}>
            Открыть форму отсмотра
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => onSkip(assignment)}>
            Невозможно посмотреть
          </Button>
        </div>
      </div>
    </Card>
  )
}

function TechnicalCasesTable({
  items,
  busyCaseId,
  noAccessBlockedUntilByStudio,
  onReview,
}: {
  items: PedagogyReviewTechnicalCase[]
  busyCaseId: number | null
  noAccessBlockedUntilByStudio: ReadonlyMap<number, string>
  onReview: (item: PedagogyReviewTechnicalCase) => void
}) {
  return (
    <Card className="overflow-hidden border border-border !p-0 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] border-separate border-spacing-0 text-[13px] leading-snug">
          <thead className="bg-muted/45 text-xs text-muted-foreground">
            <tr>
              <th className="w-[230px] border-b border-border px-3 py-2 text-left font-semibold">Проблема</th>
              <th className="w-[34%] border-b border-border px-3 py-2 text-left font-semibold">Занятие</th>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Студия / педагог</th>
              <th className="w-[225px] border-b border-border px-3 py-2 text-right font-semibold">Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              // Бейдж — действующая причина (её применяет выдача); исходная специалиста — подписью, если руководитель её изменил.
              const reasonChanged = item.effectiveReasonCode !== item.reasonCode
              const noAccessBlockedUntil = pedagogyReasonPausesStudio(item.effectiveReasonCode)
                ? noAccessBlockedUntilByStudio.get(item.studioInternalId) ?? null
                : null
              const busy = busyCaseId === item.caseId
              const signalName = item.status === 'open'
                ? item.lastReportedByName ?? 'сотрудник'
                : item.decidedByName ? `Проверил: ${item.decidedByName}` : 'Проверено'
              const signalAt = item.status === 'open' ? item.lastReportedAt : item.decidedAt ?? item.lastReportedAt
              const lessonDetails = [item.directionNameRaw, item.groupNameRaw].filter(Boolean).join(' · ') || 'Направление и группа не указаны'

              return (
                <tr key={item.caseId} className="bg-card transition-colors hover:bg-muted/20">
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <ToneBadge size="md" tone={PEDAGOGY_SKIP_REASON_TONE[item.effectiveReasonCode]} className="max-w-[170px] truncate">
                        {technicalReasonShortLabel(item.effectiveReasonCode)}
                      </ToneBadge>
                      {item.reportCount > 1 && (
                        <span className="text-[11px] tabular-nums text-muted-foreground">×{item.reportCount}</span>
                      )}
                    </div>
                    {reasonChanged && (
                      <div className="mt-1 truncate text-[11px] text-muted-foreground">
                        Специалист: {technicalReasonShortLabel(item.reasonCode)}
                      </div>
                    )}
                    <div className="mt-1 min-w-0 truncate text-[11px] text-muted-foreground" title={`${signalName} · ${formatDateTime(signalAt)}`}>
                      {signalName} · {formatDateTime(signalAt)}
                    </div>
                    {item.reportedDetails && (
                      <div className="mt-1 line-clamp-2 text-xs text-foreground" title={item.reportedDetails}>
                        {item.reportedDetails}
                      </div>
                    )}
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="font-medium text-foreground">
                      {formatDate(item.trainingDate)}, {formatTime(item.trainingTime)}
                    </div>
                    <div className="mt-0.5 min-w-0 truncate text-xs text-muted-foreground" title={lessonDetails}>
                      {lessonDetails}
                    </div>
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="min-w-0 truncate font-medium text-foreground" title={item.studioAddress ?? undefined}>
                      {item.cityName ? `${item.cityName} · ` : ''}
                      {item.studioAddress || `студия №${item.studioNumber}`}
                    </div>
                    <div className="mt-0.5 min-w-0 truncate text-xs text-muted-foreground" title={item.teacherNameRaw ?? undefined}>
                      Педагог: {item.teacherNameRaw || 'не указан'}
                    </div>
                    {noAccessBlockedUntil && (
                      <div
                        className="mt-1 inline-flex max-w-full items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                        title={`Занятия студии не выдаются до ${formatDateTime(noAccessBlockedUntil)}`}
                      >
                        <CalendarClock className="h-3 w-3 shrink-0" />
                        <span className="truncate">Не выдаём до {formatDateTime(noAccessBlockedUntil)}</span>
                      </div>
                    )}
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 text-right align-middle">
                    <ActionButton
                      disabled={busy}
                      onClick={() => onReview(item)}
                      icon={<ShieldCheck className="h-3.5 w-3.5" />}
                    >
                      {item.status === 'open' ? 'Проверить причину' : 'Пересмотреть'}
                    </ActionButton>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function priorityRequestCriteriaSummary(item: PedagogyPriorityRequestRegistryRow): string {
  const parts = [
    item.directionName,
    item.studioAddress || (item.studioNumber ? `студия №${item.studioNumber}` : null),
    item.trainingDate ? formatDate(item.trainingDate) : null,
    item.trainingTime ? formatTime(item.trainingTime) : null,
  ].filter(Boolean)

  if (parts.length > 0) return parts.join(' · ')
  return item.teacherName ? 'Любое подходящее занятие педагога' : 'Любая подходящая тренировка франчайзи'
}

function PriorityRequestsTable({
  items,
  busyRequestKey,
  onOpenReview,
  onCancel,
}: {
  items: PedagogyPriorityRequestRegistryRow[]
  busyRequestKey: string | null
  onOpenReview: (item: PedagogyPriorityRequestRegistryRow) => void
  onCancel: (item: PedagogyPriorityRequestRegistryRow) => void
}) {
  return (
    <Card className="overflow-hidden border border-border !p-0 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] table-fixed border-separate border-spacing-0 text-[13px] leading-snug">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[27%]" />
            <col className="w-[28%]" />
            <col className="w-[25%]" />
          </colgroup>
          <thead className="bg-muted/45 text-xs text-muted-foreground">
            <tr>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Запрос</th>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Франчайзи / педагог</th>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Уточнения</th>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Состояние / действие</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const failure = item.status === 'failed' ? priorityFailureLabel(item.lastFailureCode) : null
              const stateAt = item.status === 'assigned'
                ? item.assignedAt
                : item.status === 'done' || item.status === 'failed' || item.status === 'cancelled'
                  ? item.completedAt
                  : null

              return (
                <tr key={item.requestKey} className="bg-card transition-colors hover:bg-muted/20">
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="min-w-0 truncate text-sm font-semibold text-foreground"
                        title={item.requestedByName ?? undefined}
                      >
                        {item.requestedByName || (item.requestSource === 'franchisee' ? 'Франчайзи' : 'Руководитель')}
                      </span>
                      <Badge variant="neutral" className="shrink-0 whitespace-nowrap text-[11px]">
                        {item.requestSource === 'franchisee' ? 'Франчайзи' : 'Руководитель'}
                      </Badge>
                    </div>
                    <div className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
                      {formatDateTime(item.createdAt)}
                      {item.availableFrom && item.availableFrom > localIsoDate() && (
                        <div className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
                          Активен с {formatDate(item.availableFrom)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="min-w-0 truncate font-medium text-foreground" title={item.partnerName}>
                      {item.partnerName}
                    </div>
                    <div className="mt-0.5 min-w-0 truncate text-xs text-muted-foreground" title={item.teacherName ?? undefined}>
                      {item.teacherName ? `Педагог: ${item.teacherName}` : 'Любой педагог'}
                    </div>
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="min-w-0 truncate text-foreground" title={priorityRequestCriteriaSummary(item)}>
                      {priorityRequestCriteriaSummary(item)}
                    </div>
                  </td>
                  <td className="border-b border-border/70 px-3 py-2 align-middle">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant={priorityStatusVariant(item.status)} className="whitespace-nowrap text-[11px]">
                            {priorityStatusLabel(item.status)}
                          </Badge>
                          {stateAt && <span className="text-[11px] text-muted-foreground">{formatDateTime(stateAt)}</span>}
                        </div>
                        {failure && (
                          <div className="mt-1 max-w-[270px] truncate text-[11px] text-error" title={failure}>{failure}</div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                        {item.reviewId != null && item.status === 'done' && (
                          <ActionButton
                            tone="primary"
                            onClick={() => onOpenReview(item)}
                            icon={<Eye className="h-3.5 w-3.5" />}
                          >
                            Открыть
                          </ActionButton>
                        )}
                        {item.requestSource === 'lead' && item.status === 'open' && (
                          <ActionButton
                            disabled={busyRequestKey === item.requestKey}
                            onClick={() => onCancel(item)}
                          >
                            Отменить
                          </ActionButton>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

export function PedagogyReviewQueuePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAdvanced, isPedagogyLead } = usePedagogyAccess()
  const { isSuperAdmin } = useSuperAdmin()
  const periods = useStudioPeriods()
  const canManageTechnicalQueue = isAdvanced || isPedagogyLead || isSuperAdmin === true
  const canManagePriorityRequests = canManageTechnicalQueue
  const returnedFromSave = Boolean((location.state as { pedagogyReviewSaved?: boolean } | null)?.pedagogyReviewSaved)

  const [workload, setWorkload] = useState<PedagogyReviewWorkload | null>(null)
  const [assignments, setAssignments] = useState<PedagogyReviewAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(returnedFromSave ? 'Отсмотр сохранён' : null)
  const [busy, setBusy] = useState(false)
  const [skipTarget, setSkipTarget] = useState<PedagogyReviewAssignment | null>(null)
  const [skipReason, setSkipReason] = useState<PedagogyReviewSkipReason | null>(null)
  const [skipDetails, setSkipDetails] = useState('')
  const [technicalFilter, setTechnicalFilter] = useState<TechnicalFilter>('all')
  const [managementTab, setManagementTab] = useState<ManagerWorkspaceTab>(() =>
    new URLSearchParams(location.search).get('tab') === 'requests' ? 'requests' : 'technical'
  )
  const [openTechnicalCases, setOpenTechnicalCases] = useState<PedagogyReviewTechnicalCase[]>([])
  const [excludedTechnicalCases, setExcludedTechnicalCases] = useState<PedagogyReviewTechnicalCase[]>([])
  const [technicalLoading, setTechnicalLoading] = useState(false)
  const [technicalError, setTechnicalError] = useState<string | null>(null)
  const [busyCaseId, setBusyCaseId] = useState<number | null>(null)
  const [decisionTarget, setDecisionTarget] = useState<PedagogyReviewTechnicalCase | null>(null)
  const [verificationResult, setVerificationResult] = useState<PedagogyReviewTechnicalVerificationResult | null>(null)
  const [verifiedReason, setVerifiedReason] = useState<PedagogyReviewSkipReason | null>(null)
  const [decisionAction, setDecisionAction] = useState<PedagogyReviewTechnicalCaseAction | null>(null)
  const [decisionComment, setDecisionComment] = useState('')
  const [decisionApplyToStudio, setDecisionApplyToStudio] = useState(false)
  const [priorityPartners, setPriorityPartners] = useState<PedagogyPriorityPartnerOption[]>([])
  const [priorityRequests, setPriorityRequests] = useState<PedagogyPriorityRequestRegistryRow[]>([])
  const [priorityLoading, setPriorityLoading] = useState(false)
  const [priorityPartnersLoading, setPriorityPartnersLoading] = useState(false)
  const [priorityRegistryError, setPriorityRegistryError] = useState<string | null>(null)
  const [prioritySheetError, setPrioritySheetError] = useState<string | null>(null)
  const [prioritySheetOpen, setPrioritySheetOpen] = useState(false)
  const [priorityPartnerNumber, setPriorityPartnerNumber] = useState('')
  const [priorityOptions, setPriorityOptions] = useState<PedagogyPriorityManagementOptions | null>(null)
  const [priorityFilters, setPriorityFilters] = useState<PriorityReviewRequestFilters>(EMPTY_PRIORITY_FILTERS)
  const [priorityBusy, setPriorityBusy] = useState(false)
  const [busyPriorityRequestKey, setBusyPriorityRequestKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [nextWorkload, nextAssignments] = await Promise.all([
        getMyPedagogyReviewWorkload(),
        listMyPedagogyReviewAssignments(false),
      ])
      setWorkload(nextWorkload)
      setAssignments(nextAssignments)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const loadTechnicalQueue = useCallback(async () => {
    if (!canManageTechnicalQueue) {
      setOpenTechnicalCases([])
      setExcludedTechnicalCases([])
      return
    }

    setTechnicalLoading(true)
    setTechnicalError(null)
    try {
      const loadAllCases = async (status: 'open' | 'excluded') => {
        const result: PedagogyReviewTechnicalCase[] = []
        let offset = 0
        while (true) {
          const page = await listPedagogyReviewTechnicalCases(status, 200, offset)
          result.push(...page)
          const total = page[0]?.totalCount ?? result.length
          if (page.length === 0 || result.length >= total) break
          offset += page.length
        }
        return result
      }

      const [openCases, excludedCases] = await Promise.all([
        loadAllCases('open'),
        loadAllCases('excluded'),
      ])
      setOpenTechnicalCases(openCases)
      setExcludedTechnicalCases(excludedCases)
    } catch (loadError) {
      setTechnicalError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setTechnicalLoading(false)
    }
  }, [canManageTechnicalQueue])

  useEffect(() => {
    void loadTechnicalQueue()
  }, [loadTechnicalQueue])

  useEffect(() => {
    if (!canManagePriorityRequests || periods.loading || !periods.currentPeriod) return
    const today = getLocalIsoDate()
    if (periods.currentPeriod.period_start <= today) return

    let latestAllowedIndex = -1
    periods.availablePeriods.forEach((period, index) => {
      if (period.period_start <= today) latestAllowedIndex = index
    })
    if (latestAllowedIndex >= 0) periods.setPeriodByIndex(latestAllowedIndex)
  }, [
    canManagePriorityRequests,
    periods.availablePeriods,
    periods.currentPeriod,
    periods.loading,
    periods.setPeriodByIndex,
  ])

  const selectedPriorityPeriodId = periods.currentPeriod?.period_id ?? null

  const loadPriorityRequests = useCallback(async () => {
    if (!canManagePriorityRequests || selectedPriorityPeriodId == null) {
      setPriorityPartners([])
      setPriorityRequests([])
      return
    }

    setPriorityLoading(true)
    setPriorityRegistryError(null)
    try {
      const requests: PedagogyPriorityRequestRegistryRow[] = []
      let offset = 0
      while (true) {
        const page = await listPedagogyPriorityRequests(selectedPriorityPeriodId, 500, offset)
        requests.push(...page)
        const total = page[0]?.totalCount ?? requests.length
        if (page.length === 0 || requests.length >= total) break
        offset += page.length
      }
      setPriorityRequests(requests)
    } catch (loadError) {
      setPriorityRegistryError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setPriorityLoading(false)
    }
  }, [canManagePriorityRequests, selectedPriorityPeriodId])

  useEffect(() => {
    void loadPriorityRequests()
  }, [loadPriorityRequests])

  useEffect(() => {
    if (!prioritySheetOpen || !priorityPartnerNumber) {
      setPriorityOptions(null)
      return
    }

    let cancelled = false
    setPrioritySheetError(null)
    void getPedagogyPriorityRequestManagementOptions(Number(priorityPartnerNumber))
      .then(options => {
        if (!cancelled) setPriorityOptions(options)
      })
      .catch(loadError => {
        if (!cancelled) setPrioritySheetError(loadError instanceof Error ? loadError.message : String(loadError))
      })

    return () => {
      cancelled = true
    }
  }, [priorityPartnerNumber, prioritySheetOpen])

  useEffect(() => {
    const unread = assignments.filter(item => item.readAt == null && item.status === 'open')
    if (unread.length === 0) return
    void Promise.all(unread.map(item => markPedagogyReviewAssignmentRead(item.assignmentKey))).catch(() => {
      // Отметка о прочтении не влияет на возможность выполнить задание.
    })
  }, [assignments])

  const openAssignments = useMemo(
    () => assignments.filter(item => item.status === 'open' || item.status === 'in_progress'),
    [assignments]
  )

  const handleOpen = useCallback((assignment: PedagogyReviewAssignment) => {
    const params = new URLSearchParams({
      returnTo: pedagogyPaths.queue,
      assignmentKey: assignment.assignmentKey,
    })
    navigate(`${pedagogyPaths.reviewNew}?${params.toString()}`)
  }, [navigate])

  const handleRequestAssignment = useCallback(async () => {
    setBusy(true)
    setNotice(null)
    setError(null)
    try {
      const result = await requestNextPedagogyReviewAssignment()
      setNotice(result.granted ? 'Задание готово' : result.reason ?? 'Нового задания сейчас нет')
      await load()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError))
    } finally {
      setBusy(false)
    }
  }, [load])

  const handleSkipConfirm = useCallback(async () => {
    if (!skipTarget || !skipReason) return
    if (skipReason === 'queue_selection_error' && !skipDetails.trim()) {
      setError('Опишите, почему занятие или студия не должны попадать в выдачу')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const closed = await reportPedagogyReviewTechnicalIssue(skipTarget.assignmentKey, skipReason, skipDetails)
      setNotice(
        closed
          ? 'Задание исключено из выдачи и передано руководителю. Можно получить другое.'
          : 'Это задание уже закрыто'
      )
      setSkipTarget(null)
      setSkipReason(null)
      setSkipDetails('')
      await Promise.all([load(), loadTechnicalQueue()])
    } catch (skipError) {
      setError(skipError instanceof Error ? skipError.message : String(skipError))
    } finally {
      setBusy(false)
    }
  }, [skipTarget, skipReason, skipDetails, load, loadTechnicalQueue])

  const openTechnicalDecision = useCallback((item: PedagogyReviewTechnicalCase) => {
    setDecisionTarget(item)
    setVerificationResult(null)
    setVerifiedReason(null)
    setDecisionAction(null)
    setDecisionComment('')
    setDecisionApplyToStudio(false)
    setTechnicalError(null)
  }, [])

  const handleTechnicalResolve = useCallback(async () => {
    if (!decisionTarget || !verificationResult || !decisionAction) return
    const nextVerifiedReason = verificationResult === 'not_confirmed'
      ? null
      : verificationResult === 'confirmed'
        ? decisionTarget.reasonCode
        : verifiedReason
    if (verificationResult === 'changed' && !nextVerifiedReason) {
      setTechnicalError('Выберите исправленную причину')
      return
    }
    const commentRequired = verificationResult !== 'confirmed' || nextVerifiedReason === 'queue_selection_error'
    if (commentRequired && !decisionComment.trim()) {
      setTechnicalError('Добавьте комментарий к решению')
      return
    }

    setBusyCaseId(decisionTarget.caseId)
    setTechnicalError(null)
    try {
      const resolved = await resolvePedagogyReviewTechnicalCase({
        caseId: decisionTarget.caseId,
        verificationResult,
        verifiedReason: nextVerifiedReason,
        action: decisionAction,
        comment: decisionComment,
        applyToStudio: decisionApplyToStudio && verificationResult !== 'not_confirmed',
      })
      setNotice(resolved > 1
        ? `Решение применено ко всем пропускам студии на проверке: ${resolved}`
        : resolved > 0
        ? decisionAction === 'return_to_queue'
          ? 'Сигнал проверен, занятие возвращено в выдачу'
          : decisionAction === 'pause_studio'
            ? 'Причина проверена, студия приостановлена на пять дней'
            : decisionAction === 'exclude_lesson_request_studio'
              ? 'Занятие исключено, просьба убрать студию отправлена разработчикам'
              : 'Причина проверена, занятие исключено из выдачи'
        : 'Случай уже обработан')
      if (resolved) setDecisionTarget(null)
      await Promise.all([loadTechnicalQueue(), load()])
    } catch (resolveError) {
      setTechnicalError(resolveError instanceof Error ? resolveError.message : String(resolveError))
    } finally {
      setBusyCaseId(null)
    }
  }, [decisionTarget, verificationResult, verifiedReason, decisionAction, decisionComment, decisionApplyToStudio, load, loadTechnicalQueue])

  const selectedPriorityPartner = useMemo(
    () => priorityPartners.find(partner => String(partner.partnerNumber) === priorityPartnerNumber) ?? null,
    [priorityPartnerNumber, priorityPartners]
  )

  const priorityMatchingSessions = useMemo(() => {
    if (!priorityOptions) return []
    return priorityOptions.sessions
      .filter(session => !priorityFilters.studioInternalId || String(session.studioInternalId) === priorityFilters.studioInternalId)
      .filter(session => !priorityFilters.teacherId || session.teacherId === priorityFilters.teacherId)
      .filter(session => !priorityFilters.directionName || session.directionName === priorityFilters.directionName)
      .filter(session => !priorityFilters.trainingDate || session.trainingDate === priorityFilters.trainingDate)
      .filter(session => !priorityFilters.itemId || String(session.itemId) === priorityFilters.itemId)
  }, [priorityFilters, priorityOptions])

  const openPrioritySheet = useCallback(() => {
    setPriorityPartnerNumber('')
    setPriorityOptions(null)
    setPriorityFilters(EMPTY_PRIORITY_FILTERS)
    setPrioritySheetError(null)
    setPrioritySheetOpen(true)
    setPriorityPartnersLoading(true)
    void listPedagogyPriorityRequestPartners()
      .then(setPriorityPartners)
      .catch(loadError => setPrioritySheetError(loadError instanceof Error ? loadError.message : String(loadError)))
      .finally(() => setPriorityPartnersLoading(false))
  }, [])

  const handleCreatePriorityRequest = useCallback(async () => {
    if (!priorityPartnerNumber) {
      setPrioritySheetError('Сначала выберите франчайзи')
      return
    }
    const isDeferred = priorityFilters.deferredActivation
    if (!priorityOptions || (isDeferred && !priorityFilters.availableFrom)) {
      setPrioritySheetError(isDeferred ? 'Укажите дату активации' : 'Подходящих занятий за последние 7 дней нет')
      return
    }
    if (!isDeferred && priorityMatchingSessions.length === 0) {
      setPrioritySheetError('Подходящих занятий за последние 7 дней нет')
      return
    }

    setPriorityBusy(true)
    setPrioritySheetError(null)
    try {
      await createPedagogyPriorityRequest({
        partnerNumber: Number(priorityPartnerNumber),
        studioInternalId: priorityFilters.studioInternalId ? Number(priorityFilters.studioInternalId) : null,
        teacherId: priorityFilters.teacherId || null,
        directionName: priorityFilters.directionName || null,
        trainingDate: priorityFilters.trainingDate || null,
        itemId: priorityFilters.itemId ? Number(priorityFilters.itemId) : null,
        availableFrom: isDeferred ? priorityFilters.availableFrom : null,
      })
      setPrioritySheetOpen(false)
      setNotice('Отсмотр поставлен вперёд очереди')
      await loadPriorityRequests()
    } catch (createError) {
      setPrioritySheetError(createError instanceof Error ? createError.message : String(createError))
    } finally {
      setPriorityBusy(false)
    }
  }, [loadPriorityRequests, priorityFilters, priorityMatchingSessions.length, priorityOptions, priorityPartnerNumber])

  const handleCancelPriorityRequest = useCallback(async (item: PedagogyPriorityRequestRegistryRow) => {
    if (item.requestSource !== 'lead' || item.status !== 'open') return
    if (!window.confirm('Отменить этот запрос руководителя до выдачи задания?')) return

    setBusyPriorityRequestKey(item.requestKey)
    setPriorityRegistryError(null)
    try {
      const cancelled = await cancelPedagogyPriorityRequest(item.requestKey)
      setNotice(cancelled ? 'Приоритетный запрос отменён' : 'Запрос уже нельзя отменить')
      await loadPriorityRequests()
    } catch (cancelError) {
      setPriorityRegistryError(cancelError instanceof Error ? cancelError.message : String(cancelError))
    } finally {
      setBusyPriorityRequestKey(null)
    }
  }, [loadPriorityRequests])

  const noAccessBlockedUntilByStudio = useMemo(() => {
    const now = Date.now()
    const result = new Map<number, string>()
    for (const item of [...openTechnicalCases, ...excludedTechnicalCases]) {
      if (!pedagogyReasonPausesStudio(item.effectiveReasonCode) || !item.pauseUntil) continue
      const blockedUntil = new Date(item.pauseUntil)
      if (blockedUntil.getTime() <= now) continue
      const current = result.get(item.studioInternalId)
      if (!current || new Date(current).getTime() < blockedUntil.getTime()) {
        result.set(item.studioInternalId, blockedUntil.toISOString())
      }
    }
    return result
  }, [excludedTechnicalCases, openTechnicalCases])

  if (loading && assignments.length === 0 && !workload) {
    return (
      <div className="space-y-4 pb-24">
        <Card>
          <div className="py-6 text-center text-sm text-muted-foreground">Загружаем задание…</div>
        </Card>
      </div>
    )
  }

  const doneCount = workload?.doneCount ?? 0
  const targetCount = workload?.targetCount ?? 0
  const planCompleted = targetCount > 0 && doneCount >= targetCount
  const openTechnicalTotal = openTechnicalCases[0]?.totalCount ?? openTechnicalCases.length
  const excludedTechnicalTotal = excludedTechnicalCases[0]?.totalCount ?? excludedTechnicalCases.length
  const technicalCases = technicalFilter === 'excluded' ? excludedTechnicalCases : openTechnicalCases
  const filteredTechnicalCases = technicalFilter === 'all' || technicalFilter === 'excluded'
    ? technicalCases
    : openTechnicalCases.filter(item => item.effectiveReasonCode === technicalFilter)
  const technicalReasonCounts = SKIP_REASONS.reduce<Record<PedagogyReviewSkipReason, number>>((acc, reason) => {
    acc[reason.code] = openTechnicalCases.filter(item => item.effectiveReasonCode === reason.code).length
    return acc
  }, {
    recording_problem: 0,
    no_access: 0,
    no_camera: 0,
    lesson_mismatch: 0,
    queue_selection_error: 0,
  })
  return (
    <div className="space-y-4 pb-24">
      {error && (
        <Card className="border border-error/40">
          <div className="flex items-start gap-2 text-sm text-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {notice && (
        <Card>
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <span>{notice}</span>
          </div>
        </Card>
      )}

      {workload && (
        <Card className="border border-border !px-4 !py-3 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-2.5">
              <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 text-sm">
                {workload.isEvaluator ? (
                  <>
                    <span className="font-medium text-foreground">Задания на отсмотр</span>
                    <span className="text-muted-foreground"> · {doneCount} из {targetCount} за неделю</span>
                    <span className="hidden text-muted-foreground md:inline"> · {formatDate(workload.weekStart)}—{formatDate(workload.weekEnd)}</span>
                    <span className="text-muted-foreground"> · {openAssignments.length > 0 ? 'есть активное задание' : 'активного задания нет'}</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">Выдача заданий</span>
                    <span className="text-muted-foreground"> · {workload.unavailableReason ?? 'для вашей роли отключена'}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {openAssignments[0] ? (
                <ActionButton tone="primary" onClick={() => handleOpen(openAssignments[0])}>
                  Открыть задание
                </ActionButton>
              ) : workload.isEvaluator && workload.canRequestAssignment ? (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={handleRequestAssignment}
                  className="inline-flex shrink-0 items-center justify-center whitespace-nowrap"
                >
                  <Plus size={16} className="mr-1" />
                  {busy ? 'Подбираем…' : planCompleted ? 'Получить сверх плана' : 'Получить задание'}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      )}

      {openAssignments.map(assignment => (
        <AssignmentCard
          key={assignment.assignmentKey}
          assignment={assignment}
          onOpen={handleOpen}
          onSkip={target => {
            setSkipTarget(target)
            setSkipReason(null)
            setSkipDetails('')
          }}
        />
      ))}

      {canManagePriorityRequests && (
        <section className="space-y-3 pt-1" aria-label="Управление очередью отсмотров">
          <div className="border-b border-border">
            <div className="flex items-end gap-6 overflow-x-auto" role="tablist" aria-label="Управление очередью отсмотров">
              {([
                { key: 'technical' as const, label: 'Технические случаи', count: openTechnicalTotal },
                { key: 'requests' as const, label: 'Запросы на отсмотр', count: priorityRequests.length },
              ]).map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={managementTab === tab.key}
                  onClick={() => setManagementTab(tab.key)}
                  className={`shrink-0 border-b-2 px-0.5 pb-2.5 pt-1 text-sm font-medium transition-colors ${
                    managementTab === tab.key
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-1.5 text-xs tabular-nums ${managementTab === tab.key ? 'text-primary' : 'text-muted-foreground'}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {managementTab === 'technical' && (
            <>
              <div className="overflow-x-auto rounded-xl border border-border bg-card px-2.5 py-2 scrollbar-hide">
                <div className="flex min-w-max items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setTechnicalFilter('all')}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${technicalFilter === 'all' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted/50'}`}
                  >
                    Все {openTechnicalTotal}
                  </button>
                  {SKIP_REASONS.map(reason => (
                    <button
                      key={reason.code}
                      type="button"
                      onClick={() => setTechnicalFilter(reason.code)}
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${technicalFilter === reason.code ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted/50'}`}
                    >
                      <span className="inline-flex items-center gap-1.5"><ToneDot tone={PEDAGOGY_SKIP_REASON_TONE[reason.code]} />{technicalReasonShortLabel(reason.code)} {technicalReasonCounts[reason.code]}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setTechnicalFilter('excluded')}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${technicalFilter === 'excluded' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted/50'}`}
                  >
                    Исключённые {excludedTechnicalTotal}
                  </button>
                </div>
              </div>

              {technicalError && (
                <Card className="border border-error/40 !px-3 !py-2.5">
                  <div className="flex items-start gap-2 text-sm text-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{technicalError}</span>
                  </div>
                </Card>
              )}

              {technicalLoading && technicalCases.length === 0 ? (
                <Card className="!py-4 text-center text-sm text-muted-foreground">Загружаем техническую очередь…</Card>
              ) : filteredTechnicalCases.length === 0 ? (
                <Card className="!py-4 text-center text-sm text-muted-foreground">
                  {technicalFilter === 'excluded'
                    ? 'Исключённых случаев пока нет.'
                    : technicalFilter === 'all'
                      ? 'Открытых технических случаев нет.'
                      : 'В этой категории случаев сейчас нет.'}
                </Card>
              ) : (
                <TechnicalCasesTable
                  items={filteredTechnicalCases}
                  busyCaseId={busyCaseId}
                  noAccessBlockedUntilByStudio={noAccessBlockedUntilByStudio}
                  onReview={openTechnicalDecision}
                />
              )}
            </>
          )}

          {managementTab === 'requests' && (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="w-full rounded-xl border border-border bg-card px-2.5 sm:w-[390px]">
                  {periods.loading ? (
                    <div className="py-3 text-center text-xs text-muted-foreground">Загружаем периоды…</div>
                  ) : periods.currentPeriod ? (
                    <PeriodSelector
                      currentPeriod={periods.currentPeriod}
                      onPrevPeriod={periods.goToPrevPeriod}
                      onNextPeriod={periods.goToNextPeriod}
                      canGoPrev={periods.canGoPrev}
                      canGoNext={periods.canGoNext}
                      isCurrentPeriod={periods.isCurrentPeriod}
                      compact
                      showPeriodLength={false}
                    />
                  ) : (
                    <div className="py-3 text-center text-xs text-muted-foreground">Период не найден</div>
                  )}
                </div>

                {periods.isCurrentPeriod && (
                  <ActionButton tone="primary" onClick={openPrioritySheet} icon={<Flag className="h-3.5 w-3.5" />}>
                    Поставить отсмотр вперёд
                  </ActionButton>
                )}
              </div>

              {periods.error && (
                <Card className="border border-error/40 !px-3 !py-2.5">
                  <div className="flex items-start gap-2 text-sm text-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Не удалось загрузить периоды: {periods.error}</span>
                  </div>
                </Card>
              )}

              {priorityRegistryError && !prioritySheetOpen && (
                <Card className="border border-error/40 !px-3 !py-2.5">
                  <div className="flex items-start gap-2 text-sm text-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{priorityRegistryError}</span>
                  </div>
                </Card>
              )}

              {priorityLoading && priorityRequests.length === 0 ? (
                <Card className="!py-4 text-center text-sm text-muted-foreground">Загружаем запросы…</Card>
              ) : priorityRequests.length === 0 ? (
                <Card className="!py-4 text-center text-sm text-muted-foreground">Запросов за выбранный период пока нет.</Card>
              ) : (
                <PriorityRequestsTable
                  items={priorityRequests}
                  busyRequestKey={busyPriorityRequestKey}
                  onOpenReview={request => {
                    if (request.reviewId == null) return
                    const returnParams = new URLSearchParams(location.search)
                    returnParams.set('tab', 'requests')
                    const returnSearch = returnParams.toString()
                    const returnTo = `${location.pathname}${returnSearch ? `?${returnSearch}` : ''}`
                    navigate(`${pedagogyPaths.reviewReport(String(request.reviewId))}?returnTo=${encodeURIComponent(returnTo)}`)
                  }}
                  onCancel={request => void handleCancelPriorityRequest(request)}
                />
              )}
            </>
          )}
        </section>
      )}

      {canManagePriorityRequests && (
        <BottomSheet
          isOpen={prioritySheetOpen}
          onClose={() => !priorityBusy && setPrioritySheetOpen(false)}
          title="Поставить отсмотр вперёд"
          className="sm:max-w-2xl"
        >
          <div className="space-y-4 px-4 pb-5 pt-3 sm:px-5">
            {!priorityPartnerNumber ? (
              <>
                <div className="text-sm font-medium text-foreground">Выберите франчайзи</div>

                <PriorityPartnerPicker
                  options={priorityPartners}
                  value={priorityPartnerNumber}
                  loading={priorityPartnersLoading}
                  onSelect={value => {
                    setPriorityPartnerNumber(value)
                    setPriorityOptions(null)
                    setPriorityFilters(EMPTY_PRIORITY_FILTERS)
                    setPrioritySheetError(null)
                  }}
                />

                {prioritySheetError && (
                  <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-sm text-error">
                    {prioritySheetError}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/25 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Франчайзи</div>
                    <div className="mt-0.5 truncate text-sm font-semibold text-foreground">
                      {selectedPriorityPartner?.partnerName ?? `№${priorityPartnerNumber}`}
                    </div>
                    {selectedPriorityPartner && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Доступно занятий: {selectedPriorityPartner.availableSessions}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={priorityBusy}
                    onClick={() => {
                      setPriorityPartnerNumber('')
                      setPriorityOptions(null)
                      setPriorityFilters(EMPTY_PRIORITY_FILTERS)
                      setPrioritySheetError(null)
                    }}
                  >
                    Изменить
                  </Button>
                </div>

                {!priorityOptions && !prioritySheetError && (
                  <div className="rounded-xl border border-border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
                    Загружаем занятия франчайзи…
                  </div>
                )}

                {priorityOptions && (
                  <PriorityReviewRequestFields
                    mode="lead"
                    sessions={priorityOptions.sessions}
                    value={priorityFilters}
                    onChange={setPriorityFilters}
                  />
                )}

                {prioritySheetError && (
                  <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-sm text-error">
                    {prioritySheetError}
                  </div>
                )}

                <div className="flex justify-end border-t border-border pt-4">
                  <Button
                    type="button"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={priorityBusy || !priorityOptions || (priorityFilters.deferredActivation ? !priorityFilters.availableFrom : priorityMatchingSessions.length === 0)}
                    onClick={() => void handleCreatePriorityRequest()}
                  >
                    {priorityBusy ? 'Сохраняем…' : 'Поставить вперёд очереди'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </BottomSheet>
      )}

      <BottomSheet
        isOpen={skipTarget != null}
        onClose={() => {
          if (busy) return
          setSkipTarget(null)
          setSkipReason(null)
          setSkipDetails('')
        }}
        title="Почему запись нельзя посмотреть"
      >
        <div className="space-y-4 px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
          <div className="rounded-xl border border-warning/40 bg-warning/5 px-3 py-3">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div>
                <div className="font-medium text-foreground">Это не просто отмена задания</div>
                <div className="mt-1 leading-5 text-muted-foreground">
                  Вы фиксируете невозможность отсмотра. Руководитель проверит причину,
                  а сигнал попадёт в статистику специалиста и франчайзи.
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {SKIP_REASONS.map(reason => {
              const selected = skipReason === reason.code
              return (
                <label
                  key={reason.code}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm text-foreground transition-colors ${
                    selected ? 'border-primary/60 bg-primary/5' : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="skip-reason"
                    value={reason.code}
                    checked={selected}
                    onChange={() => setSkipReason(reason.code)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  />
                  <span className="min-w-0 leading-5">{reason.label}<span className="mt-0.5 block text-xs text-muted-foreground">{reason.hint}</span></span>
                </label>
              )
            })}
          </div>
          {skipReason && (
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="skip-details">
                {skipReason === 'queue_selection_error'
                  ? 'Почему занятие или студия не должны выдаваться'
                  : 'Пояснение для руководителя (необязательно)'}
              </label>
              <textarea
                id="skip-details"
                value={skipDetails}
                onChange={event => setSkipDetails(event.target.value)}
                maxLength={2000}
                placeholder={skipReason === 'queue_selection_error'
                  ? 'Например: дубль, уже отсмотрено, неверное направление или педагог; студия закрыта или работает онлайн…'
                  : 'Что именно вы проверили и что не сработало'}
                className="mt-2 min-h-24 w-full resize-y rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
          )}
          <div className="pt-1">
            <Button
              type="button"
              size="sm"
              disabled={busy || !skipReason || (skipReason === 'queue_selection_error' && !skipDetails.trim())}
              onClick={handleSkipConfirm}
              className="w-full sm:w-auto"
            >
              Передать на проверку руководителю
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={decisionTarget != null}
        onClose={() => {
          if (busyCaseId != null) return
          setDecisionTarget(null)
          setTechnicalError(null)
        }}
        title="Проверка причины пропуска"
        className="sm:max-w-2xl"
      >
        {decisionTarget && (
          <div className="space-y-5 px-4 pb-5 pt-4 sm:px-5">
            <div className="rounded-xl border border-border bg-muted/25 px-3 py-3 text-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Сообщил специалист</div>
              <div className="mt-1 font-semibold text-foreground">{decisionTarget.reasonLabel}</div>
              {decisionTarget.reportedDetails && (
                <div className="mt-2 whitespace-pre-wrap text-muted-foreground">{decisionTarget.reportedDetails}</div>
              )}
              <div className="mt-2 text-xs text-muted-foreground">
                {decisionTarget.lastReportedByName ?? 'Сотрудник'} · {formatDateTime(decisionTarget.lastReportedAt)}
              </div>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-foreground">Результат проверки</legend>
              {([
                ['confirmed', 'Причина подтверждена'],
                ['changed', 'Причина указана неверно — изменить'],
                ['not_confirmed', 'Причина не подтвердилась'],
              ] as const).map(([value, label]) => (
                <label key={value} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm ${verificationResult === value ? 'border-primary/60 bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <input
                    type="radio"
                    name="verification-result"
                    value={value}
                    checked={verificationResult === value}
                    onChange={() => {
                      setVerificationResult(value)
                      setVerifiedReason(value === 'confirmed' ? decisionTarget.reasonCode : null)
                      setDecisionAction(null)
                    }}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            {verificationResult === 'changed' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold text-foreground">Правильная причина</legend>
                {SKIP_REASONS
                  .filter(reason => reason.code !== decisionTarget.reasonCode)
                  .map(reason => (
                    <label key={reason.code} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm ${verifiedReason === reason.code ? 'border-primary/60 bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                      <input
                        type="radio"
                        name="verified-reason"
                        value={reason.code}
                        checked={verifiedReason === reason.code}
                        onChange={() => {
                          setVerifiedReason(reason.code)
                          setDecisionAction(null)
                        }}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      />
                      <span>{reason.label}</span>
                    </label>
                  ))}
              </fieldset>
            )}

            {verificationResult && (verificationResult !== 'changed' || verifiedReason) && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold text-foreground">Действие с очередью</legend>
                {(() => {
                  const reason = verificationResult === 'not_confirmed'
                    ? null
                    : verificationResult === 'confirmed'
                      ? decisionTarget.reasonCode
                      : verifiedReason
                  const actions: Array<{ code: PedagogyReviewTechnicalCaseAction; label: string; hint: string }> = decisionTarget.status === 'excluded'
                    ? [{ code: 'return_to_queue', label: 'Вернуть в выдачу', hint: 'Снять действующее ограничение' }]
                    : verificationResult === 'not_confirmed'
                      ? [{ code: 'return_to_queue', label: 'Вернуть в выдачу', hint: 'Сигнал не подтвердился' }]
                      : reason && pedagogyReasonPausesStudio(reason)
                        ? [
                            { code: 'pause_studio', label: 'Приостановить студию на 5 дней', hint: 'Срок начнётся с этого решения' },
                            { code: 'return_to_queue', label: 'Вернуть занятие', hint: 'Доступ уже восстановлен' },
                          ]
                        : reason === 'queue_selection_error'
                            ? [
                                { code: 'exclude_lesson', label: 'Убрать это занятие', hint: 'Разработчикам придёт уведомление, занятие попадёт в список ошибок выдачи' },
                                { code: 'exclude_lesson_request_studio', label: 'Убрать занятие и попросить убрать студию', hint: 'Студия пока продолжит выдаваться — её исключение разберут разработчики по уведомлению' },
                                { code: 'return_to_queue', label: 'Вернуть занятие', hint: 'Ограничение больше не требуется' },
                              ]
                            : [
                                { code: 'exclude_lesson', label: 'Убрать это занятие', hint: 'Другие занятия студии продолжат выдаваться' },
                                { code: 'return_to_queue', label: 'Вернуть занятие', hint: 'Ограничение больше не требуется' },
                              ]
                  return actions.map(action => (
                    <label key={action.code} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm ${decisionAction === action.code ? 'border-primary/60 bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                      <input
                        type="radio"
                        name="decision-action"
                        value={action.code}
                        checked={decisionAction === action.code}
                        onChange={() => setDecisionAction(action.code)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      />
                      <span><span className="block font-medium text-foreground">{action.label}</span><span className="mt-0.5 block text-xs text-muted-foreground">{action.hint}</span></span>
                    </label>
                  ))
                })()}
              </fieldset>
            )}

            {(() => {
              // Групповое решение подтверждает чужие сигналы — только для проверенной причины открытого случая.
              if (decisionTarget.status !== 'open' || !verificationResult || verificationResult === 'not_confirmed') return null
              const otherStudioCases = openTechnicalCases.filter(item =>
                item.studioInternalId === decisionTarget.studioInternalId && item.caseId !== decisionTarget.caseId
              ).length
              if (otherStudioCases === 0) return null
              return (
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm ${decisionApplyToStudio ? 'border-primary/60 bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <input
                    type="checkbox"
                    checked={decisionApplyToStudio}
                    onChange={event => setDecisionApplyToStudio(event.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  />
                  <span>
                    <span className="block font-medium text-foreground">Применить ко всем пропускам этой студии на проверке (ещё {otherStudioCases})</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Те же причина и действие; где специалист указал другую причину, она будет отмечена как исправленная</span>
                  </span>
                </label>
              )
            })()}

            {verificationResult && (
              <div>
                <label className="text-sm font-semibold text-foreground" htmlFor="technical-decision-comment">
                  Комментарий{verificationResult !== 'confirmed' || verifiedReason === 'queue_selection_error' || decisionTarget.reasonCode === 'queue_selection_error' ? ' — обязательно' : ''}
                </label>
                <textarea
                  id="technical-decision-comment"
                  value={decisionComment}
                  onChange={event => setDecisionComment(event.target.value)}
                  maxLength={2000}
                  placeholder="Что проверено и почему принято это решение"
                  className="mt-2 min-h-24 w-full resize-y rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
              </div>
            )}

            {technicalError && (
              <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-sm text-error">{technicalError}</div>
            )}

            <div className="flex justify-end border-t border-border pt-4">
              <Button
                type="button"
                size="sm"
                disabled={busyCaseId != null || !verificationResult || !decisionAction || (verificationResult === 'changed' && !verifiedReason)}
                onClick={() => void handleTechnicalResolve()}
                className="w-full sm:w-auto"
              >
                {busyCaseId != null ? 'Сохраняем решение…' : 'Зафиксировать проверку'}
              </Button>
            </div>
          </div>
        )}
      </BottomSheet>
    </div>
  )
}
